/**
 * ``useRevealMapSelectionInList`` — surface the synchronized list when the
 * operator selects a glyph on the map (operator request 2026-06-12; REQ-UI-051
 * / ADR-024 §5).
 *
 * # The behaviour
 *
 * Selecting a Platform / Detection / Alert glyph on the map already syncs the
 * corresponding list (highlight + scroll the inspected row) + Entity Details
 * via the two-slot selection bus (:file:`selection-store.ts` — a map-glyph
 * click calls ``selectOnMap``, setting both ``inspected`` and ``mapSelected``).
 * But if that list panel is sitting **behind another tab** in its dockview
 * group, the operator can't see the synced selection without manually switching
 * tabs. This hook closes that gap: on a map-originated selection it **activates**
 * the corresponding list panel's tab so the highlighted row is visible.
 *
 * Driven off ``mapSelected`` — the slot set **only** by ``selectOnMap`` (the
 * map-glyph/marker click + the list "Select on Map" right-click action), never
 * by a plain list-row left-click (which sets ``inspected`` only). So the reveal
 * fires exactly for geographic-focus changes, which is the operator's "I clicked
 * something on the map" gesture.
 *
 * # Reveal, never open
 *
 * The reveal uses :func:`MultiPaneApi.activatePanel`, which **only** activates a
 * panel that is already present in the layout — a background tab is surfaced,
 * but a list the operator has **closed** (or that is role-filtered out of their
 * workspace) is left closed (a no-op). This matches the request: surface a list
 * that is "included in the UI but not currently visible", without force-opening
 * one the operator removed. A no-op is also the result when no workspace is
 * mounted (``multiPaneApi === null``) or the selection is cleared
 * (``mapSelected === null``) — so deselecting never changes tabs.
 *
 * # Kind → list panel
 *
 * | ``mapSelected.kind`` | list panel     |
 * |----------------------|----------------|
 * | ``platform``         | ``platforms``  |
 * | ``aggregator``       | ``platforms``  (aggregators live in the Platforms list) |
 * | ``detection``        | ``detections`` |
 * | ``alert``            | ``alerts``     |
 *
 * Mounted once at the shell level (:class:`AppShell`, under the
 * ``MultiPaneApiProvider``) so it runs for every authenticated route; it
 * no-ops on routes without a workspace.
 *
 * Cross-references:
 *   - REQ-UI-051 (synchronized-selection bus); ADR-024 §5 (selection bus).
 *   - selection/selection-store.ts (``mapSelected`` / ``selectOnMap``).
 *   - multi-pane/multi-pane-context.ts (``activatePanel``).
 *   - docs/engineering/detail/storyboards/flows/Q-side-by-side-map-platforms/README.md.
 */
import { useEffect, useRef } from "react";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import type { PanelId } from "@/multi-pane/registry-types";
import { useMapSelected, type EntitySelection } from "@/selection/selection-store";

/** The list panel that renders each selectable entity kind. Aggregators are
 *  map-invisible (no glyph), but a "Select on Map" of one would still surface
 *  the Platforms list where it lives. Shared with :func:`useInspectInList` (the
 *  cross-pane-link reveal).
 *
 *  Keyed by :type:`EntitySelection`'s kinds rather than by every
 *  :type:`SelectionKind`: an ADR-038 ``query-record`` selection originates
 *  *in* the Query panel and has no separate list to reveal, and it never
 *  reaches the map slot at all. */
export const LIST_PANEL_FOR_KIND: Readonly<Record<EntitySelection["kind"], PanelId>> = {
  platform: "platforms",
  aggregator: "platforms",
  detection: "detections",
  alert: "alerts",
  // ADR-041 D-5 (REQ-UI-171): a map line click reveals the Tracks panel.
  track: "tracks",
};

/**
 * Activate the list panel matching the current map selection whenever it
 * changes, so a synced-but-hidden list tab is surfaced. Renders nothing —
 * call it once under the workspace's provider (the shell).
 */
export function useRevealMapSelectionInList(): void {
  const mapSelected = useMapSelected();
  const multiPaneApi = useMultiPaneApi();

  // Hold the live api in a ref so the reveal effect depends ONLY on
  // ``mapSelected`` — never on the api object's identity. The workspace
  // re-assembles its ``MultiPaneApi`` (a fresh object) on EVERY layout change,
  // including a manual tab switch (``layoutRevision`` bumps on dockview's
  // ``onDidLayoutChange``). If the effect listed ``multiPaneApi`` in its deps it
  // would re-run on each such churn and re-activate ``mapSelected``'s list,
  // reverting the operator's manual tab switch with a flicker — the reveal must
  // fire only when the *selection* changes, not when the workspace re-renders.
  const apiRef = useRef(multiPaneApi);
  apiRef.current = multiPaneApi;

  useEffect(() => {
    if (mapSelected === null) {
      return;
    }
    // The non-map kinds — a ``query-record`` (Query panel) and the four
    // ``status-*`` kinds (System Status; ADR-024 §5 amended 2026-07-24,
    // 2026-07-31 and 2026-08-05) — can never reach the map slot: ``selectOnMap`` accepts only
    // an EntitySelection, and a status selection routes through
    // ``inspectStatus`` (which clears ``mapSelected``). None has a list to
    // reveal. The guard keeps ``LIST_PANEL_FOR_KIND`` total rather than relying
    // on those invariants holding forever.
    if (
      mapSelected.kind === "query-record" ||
      mapSelected.kind === "status-component" ||
      mapSelected.kind === "status-peer" ||
      mapSelected.kind === "status-interface" ||
      mapSelected.kind === "status-connection"
    ) {
      return;
    }
    // ``activatePanel`` is a no-op when the list isn't open (closed by the
    // operator / role-filtered out / no workspace), so this only ever surfaces
    // a background tab — never force-opens a panel.
    apiRef.current?.activatePanel(LIST_PANEL_FOR_KIND[mapSelected.kind]);
  }, [mapSelected]);
}
